import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

export default function FacebookGallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // index of open photo

  useEffect(() => {
    fetch("/.netlify/functions/fb-photos")
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        const all = Array.isArray(data.photos) ? data.photos : [];
        const seen = new Set();
        setPhotos(all.filter((p) => { if (seen.has(p.full)) return false; seen.add(p.full); return true; }));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const prev = useCallback(() => {
    setLightbox((i) => (i > 0 ? i - 1 : photos.length - 1));
  }, [photos.length]);

  const next = useCallback(() => {
    setLightbox((i) => (i < photos.length - 1 ? i + 1 : 0));
  }, [photos.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, prev, next]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="aspect-square animate-pulse rounded-2xl bg-white/10" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-16 text-center">
        <Camera className="h-8 w-8 text-slate-500" />
        <p className="text-sm text-slate-400">Could not load gallery photos.</p>
        <p className="text-xs text-slate-500">{error}</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/5 py-16 text-center">
        <Camera className="h-8 w-8 text-slate-500" />
        <p className="text-sm text-slate-400">No photos found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {photos.map((photo, i) => (
          <button
            key={photo.id}
            type="button"
            onClick={() => setLightbox(i)}
            className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 bg-white/5 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          >
            <img
              src={photo.thumb}
              alt={photo.caption || "Club photo"}
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105 group-hover:brightness-75"
            />
            {photo.caption && (
              <div className="absolute inset-x-0 bottom-0 translate-y-full bg-slate-950/80 p-2 text-xs text-slate-200 transition duration-300 group-hover:translate-y-0 line-clamp-2">
                {photo.caption}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4"
          onClick={() => setLightbox(null)}
        >
          {/* Close */}
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full border border-white/20 bg-white/10 p-2 text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Prev */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 rounded-full border border-white/20 bg-white/10 p-3 text-white hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Image */}
          <div className="max-h-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={photos[lightbox].full}
              alt={photos[lightbox].caption || "Club photo"}
              className="max-h-[80vh] max-w-full rounded-2xl object-contain shadow-2xl"
            />
            {photos[lightbox].caption && (
              <p className="mt-3 text-center text-sm text-slate-300">{photos[lightbox].caption}</p>
            )}
            <p className="mt-1 text-center text-xs text-slate-500">
              {lightbox + 1} / {photos.length}
            </p>
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 rounded-full border border-white/20 bg-white/10 p-3 text-white hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </>
  );
}
